import React, { useEffect, useState, useCallback } from "react";
import { Spinner, Badge, Button, Alert } from "react-bootstrap";
import { FaEnvelope, FaSync, FaRedo } from "react-icons/fa";
import { apiFetch, hasAction } from "./utils/auth";

const API_URL = process.env.REACT_APP_API_URL;

function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}
function fmtWhen(v) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function ackBadge(status) {
  return status === "Sent" ? "success" : status === "Failed" ? "danger" : "secondary";
}

// How long an order with NO acknowledgements at all is re-checked before the panel
// stops and asks for a manual Refresh (UI-CNV-042): long enough for a fresh order's
// acks to be generated, short enough that an old order without any is not polled forever.
const EMPTY_WATCH_MS = 2 * 60 * 1000;
const POLL_MS = 6000;

// =============================================================================
// AckStatusPanel — per-PO order acknowledgements for a created order.
// One confirmation per PO ({order}-1, -2, …). Shows send status and offers a
// manual resend (idempotent: an already-Sent ack only re-sends on a fresh
// resend trigger, which these endpoints supply). Auto-refreshes while any ack
// is still Pending/Failed. "Resend all" also covers an order that has only the
// whole-order acknowledgement (no per-PO rows): the API re-queues that one instead
// of answering 404 (Issue 18).
// =============================================================================
// `bare` drops the card chrome so the panel can sit inside the Documents modal
// as a tab body. Default false, so every existing call site is untouched.
function AckStatusPanel({ orderNum, bare = false }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(null);   // confirmationNumber | "all"
  const [note, setNote]       = useState(null);
  // When the current watch of an empty order began (open, or the last manual Refresh).
  const [watchFrom, setWatchFrom] = useState(() => Date.now());
  const [gaveUp, setGaveUp]   = useState(false);
  // Re-sending emails the customer: the same customer-email action the API checks (00035).
  const canResend = hasAction("00035", true);

  const load = useCallback(async () => {
    if (!orderNum) return;
    setLoading(true); setError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/OrderConfirmation/by-order/${encodeURIComponent(orderNum)}`);
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new Error(d?.message || d?.error || `HTTP ${r.status}`);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [orderNum]);

  useEffect(() => { setWatchFrom(Date.now()); setGaveUp(false); load(); }, [load]);

  // A manual Refresh also restarts the watch of an order that has none yet.
  const refresh = () => { setWatchFrom(Date.now()); setGaveUp(false); load(); };

  // Auto-poll while any ack hasn't reached Sent (render/send is async + retried).
  // An order with NO acks at all is a fresh one whose acks are still being generated —
  // it used to count as settled, so the panel never refreshed by itself (UI-CNV-042).
  // It is watched for EMPTY_WATCH_MS, then the panel asks for a manual Refresh.
  useEffect(() => {
    const confs = data?.confirmations || [];
    // With no per-PO rows the whole-order ack (from the archive) is the one to watch.
    const whole = data?.wholeOrderAck;
    const empty = confs.length === 0 && !whole;
    const settled = confs.length > 0 ? confs.every((c) => c.status === "Sent") : !empty && whole.status === "Sent";
    if (settled) return undefined;
    const expired = () => empty && Date.now() - watchFrom >= EMPTY_WATCH_MS;
    if (expired()) { setGaveUp(true); return undefined; }
    const id = setInterval(() => {
      if (expired()) { clearInterval(id); setGaveUp(true); return; }
      load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [data, load, watchFrom]);

  const resend = async (key, url) => {
    setBusy(key); setNote(null); setError(null);
    try {
      const r = await apiFetch(url, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || d?.error || `HTTP ${r.status}`);
      setNote(d?.mode === "whole-order" ? "Whole-order acknowledgement re-queued." : d?.count != null ? `Re-queued ${d.count} acknowledgement(s).` : "Acknowledgement re-queued.");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const confs = data?.confirmations || [];

  return (
    <div className={bare ? "" : "qw-card-flat qw-pad"} style={bare ? { padding: "14px 18px" } : { marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        <span className="qw-eyebrow" style={bare ? { display: "none" } : undefined}><FaEnvelope style={{ marginRight: 6 }} /> Acknowledgements{data?.count != null ? ` (${data.count})` : ""}</span>
        {bare && <span style={{ fontSize: 12.5, color: "#475569" }}>Sent to the customer when the order was raised. Resend if they never received it.</span>}
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="sm" variant="outline-secondary" onClick={refresh} disabled={loading}><FaSync /> Refresh</Button>
          {canResend && (confs.length > 0 || data?.wholeOrderAck) && (
            <Button size="sm" variant="outline-primary" disabled={busy === "all"} onClick={() => resend("all", `${API_URL}/api/OrderConfirmation/by-order/${encodeURIComponent(orderNum)}/resend`)}>
              {busy === "all" ? <Spinner animation="border" size="sm" /> : <FaRedo />} Resend all
            </Button>
          )}
        </div>
      </div>

      {note && <Alert variant="success" style={{ fontSize: 12.5, padding: "6px 10px" }} dismissible onClose={() => setNote(null)}>{note}</Alert>}
      {error && <Alert variant="danger" style={{ fontSize: 12.5, padding: "6px 10px" }}>{error}</Alert>}

      {loading && !data ? (
        <div style={{ fontSize: 12.5, color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading acknowledgements…</div>
      ) : confs.length === 0 && data?.wholeOrderAck ? (
        // No per-PO confirmations were planned (held or empty plan), so the customer got the
        // single whole-order acknowledgement instead. The API reports it from the document
        // archive; without this the panel claimed nothing had been sent.
        <div style={{ fontSize: 12.5, color: "#475569", padding: "7px 0", borderTop: "1px solid #f1f5f9" }}>
          <strong>Whole-order acknowledgement</strong>{" "}
          <Badge bg={data.wholeOrderAck.status === "Sent" ? "success" : "danger"} style={{ fontSize: 10.5 }}>{data.wholeOrderAck.status}</Badge>
          <div style={{ color: "#94A3B8", marginTop: 2 }}>
            {data.wholeOrderAck.generatedAt ? new Date(data.wholeOrderAck.generatedAt).toLocaleString() : ""}
            {data.wholeOrderAck.triggerSource ? ` · ${data.wholeOrderAck.triggerSource}` : ""}
            {" · no per-PO confirmations were issued for this order"}
          </div>
        </div>
      ) : confs.length === 0 ? (
        // A failed check shows only the red box above — "No acknowledgements yet" under it
        // would state something the panel could not find out (UI-CNV-042).
        error ? null : (
          <div style={{ fontSize: 12.5, color: "#94A3B8" }}>
            No acknowledgements yet — they{"’"}re generated shortly after the order is created.
            {gaveUp && <div style={{ color: "#64748B", marginTop: 4 }}>Still nothing after 2 minutes — press Refresh to check again.</div>}
          </div>
        )
      ) : (
        confs.map((c) => (
          <div key={c.confirmationNumber} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "7px 0", borderTop: "1px solid #f1f5f9", flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="qw-mono" style={{ fontWeight: 700, color: "#0F172A", fontSize: 13 }}>{c.confirmationNumber}</span>
                <Badge bg={ackBadge(c.status)}>{c.status}</Badge>
              </div>
              <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}>
                {c.customerPoNum ? `PO ${c.customerPoNum} · ` : ""}{fmtMoney(c.amount)}{c.sentAt ? ` · sent ${fmtWhen(c.sentAt)}` : ""}
              </div>
              {c.lastError && <div style={{ fontSize: 11, color: "#B91C1C", marginTop: 2 }}>{c.lastError}</div>}
            </div>
            {canResend && (
            <Button size="sm" variant="outline-secondary" disabled={busy === c.confirmationNumber}
              onClick={() => resend(c.confirmationNumber, `${API_URL}/api/OrderConfirmation/${encodeURIComponent(c.confirmationNumber)}/resend`)}>
              {busy === c.confirmationNumber ? <Spinner animation="border" size="sm" /> : <><FaRedo style={{ marginRight: 4 }} /> Resend</>}
            </Button>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default AckStatusPanel;
